/**
 * Chat effects hook
 * Handles autofocus, streaming focus, and initial query sending
 */

import { useEffect, useLayoutEffect } from 'react';
import type { UIMessage } from 'ai';
import type { FilePart } from '../../lib/db/types';

/** Chat IDs for which we have already sent the initial query (persists across Strict Mode remounts) */
const initialQuerySentForChatIds = new Set<string>();

interface InitialMessagePayload {
  text: string;
  files?: FilePart[];
  activeSkillId?: string;
}

interface UseChatEffectsProps {
  inputRef: React.RefObject<HTMLTextAreaElement | null>;
  status: string;
  initialQuery: string;
  /** Files attached before the chat existed (start-screen composer). */
  initialFiles?: FilePart[];
  /** Skill attached before the chat existed; applies to the first message. */
  initialActiveSkillId?: string | null;
  chatId: string;
  sendMessage: (message: InitialMessagePayload) => Promise<void>;
  messages: UIMessage[];
  sendMessageRef: React.MutableRefObject<(message: InitialMessagePayload) => Promise<void>>;
  messagesLengthRef: React.MutableRefObject<number>;
  /** When true, a tool-approval prompt owns focus — don't yank it back to the composer. */
  pendingToolCallConfirmation?: boolean;
}

export function useChatEffects({
  inputRef,
  status,
  initialQuery,
  initialFiles,
  initialActiveSkillId = null,
  chatId,
  sendMessage,
  messages,
  sendMessageRef,
  messagesLengthRef,
  pendingToolCallConfirmation = false,
}: UseChatEffectsProps): void {
  // Autofocus chat input on mount (e.g. when loading /chat?chatId=...)
  useEffect(() => {
    if (pendingToolCallConfirmation) return;
    inputRef.current?.focus();
    // Mount-only autofocus; pendingToolCallConfirmation intentionally read once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputRef]);

  // Focus input when a turn settles (ready/error) — but not while a tool-approval
  // prompt is showing, so keyboard focus can stay on the Allow/Deny controls.
  useEffect(() => {
    if (status === 'streaming' || status === 'submitted' || pendingToolCallConfirmation) {
      return;
    }
    inputRef.current?.focus();
  }, [status, inputRef, pendingToolCallConfirmation]);

  // Send initial query when starting a new chat from the start screen (Ask Meridian input + Start).
  // useLayoutEffect + immediate send: runs synchronously after commit so no other effect can clear us.
  // Minimal deps [initialQuery, chatId]; refs for sendMessage and message count; module Set for once-per-chat.
  useLayoutEffect(() => {
    const hasFiles = !!initialFiles && initialFiles.length > 0;
    if (
      (!initialQuery.trim() && !hasFiles) ||
      initialQuerySentForChatIds.has(chatId) ||
      messagesLengthRef.current !== 0 ||
      status === 'streaming'
    ) {
      return;
    }
    initialQuerySentForChatIds.add(chatId);
    sendMessageRef.current({
      text: initialQuery,
      ...(hasFiles ? { files: initialFiles } : {}),
      ...(initialActiveSkillId ? { activeSkillId: initialActiveSkillId } : {}),
    });
    // Intentionally minimal deps so useChat updates don't re-run and we only send once per chat
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialQuery, chatId]);
}
